import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { DirtyFieldChange, useDirtyFormGuard } from '@/hooks'

export const useSideNavTab = <T extends string>(resolveTab: (rawTab: string | null) => T) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isDirty, setIsDirty] = useState(false)
  const [getChanges, setGetChanges] = useState<(() => DirtyFieldChange[]) | undefined>()
  const guard = useDirtyFormGuard(isDirty, getChanges)

  const rawTab = searchParams.get('tab')
  const activeTab = resolveTab(rawTab)

  // Note: rewrites a missing, invalid or gated ?tab= to the resolved one
  useEffect(() => {
    if (rawTab !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true })
    }
  }, [rawTab, activeTab, setSearchParams])

  const handleDirtyChange = (dirty: boolean, getter?: () => DirtyFieldChange[]) => {
    setIsDirty(dirty)
    setGetChanges(dirty && getter ? () => getter : undefined)
  }

  const handleTabChange = (tab: T) => {
    if (tab === activeTab) return
    guard(() => {
      setIsDirty(false)
      setGetChanges(undefined)
      setSearchParams({ tab }, { replace: true })
    })
  }

  return { activeTab, handleDirtyChange, handleTabChange }
}
